import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import emailjs from 'emailjs-com';
import { EMAILJS_CONFIG } from '../config/emailjs';
import './Contact.css';

const Contact = () => {
  const location = useLocation();
  const productFromState = location.state?.product;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    message: productFromState ? `Inquiry about: ${productFromState}\n\n` : ''
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous status
    setSubmitStatus({ type: '', message: '' });
    
    // Validate form
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length === 0) {
      // Start submission
      setIsSubmitting(true);
      
      try {
        // Prepare email parameters to match EmailJS template variables
        const emailParams = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          city: formData.city || 'Not provided',
          message: formData.message
        };
        
        // Send email using EmailJS
        await emailjs.send(
          EMAILJS_CONFIG.SERVICE_ID,
          EMAILJS_CONFIG.TEMPLATE_ID,
          emailParams,
          EMAILJS_CONFIG.PUBLIC_KEY
        );
        
        // Success
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for your inquiry. We will contact you shortly.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          city: '',
          message: ''
        });
        setErrors({});
        
      } catch (error) {
        // Error
        console.error('EmailJS Error:', error);
        setSubmitStatus({
          type: 'error',
          message: 'Something went wrong. Please try again or contact us directly.'
        });
      } finally {
        // Reset submitting state
        setIsSubmitting(false);
      }
    } else {
      setErrors(newErrors);
    }
  };

  const handleWhatsAppClick = () => {
    // Placeholder number - user will edit later
    window.open('https://wa.me/919042239539', '_blank');
  };

  const handleLinkedInClick = () => {
    // Placeholder - user will edit later
    window.open('https://www.linkedin.com/in/we-2-exporters?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app', '_blank');
  };

  return (
    <div className="contact-page">
      <div className="contact-header">
        <h1>Contact Us</h1>
        <p>Let's discuss how we can support your agro product needs</p>
      </div>

      <div className="contact-container">
        {/* Left Column - Contact Info */}
        <div className="contact-info">
          <h2>Get in Touch</h2>
          
          <div className="contact-item">
            <div className="contact-icon">📞</div>
            <div className="contact-details">
              <h3>Phone</h3>
              <p>+91 9042239539</p>
            </div>
          </div>

          <div className="contact-item">
            <div className="contact-icon">📧</div>
            <div className="contact-details">
              <h3>Email</h3>
              <p>we2exporters@gmail.com</p>
            </div>
          </div>

          <button className="whatsapp-button" onClick={handleWhatsAppClick}>
            <span className="whatsapp-icon">💬</span>
            Chat on WhatsApp
          </button>

          <div className="linkedin-section">
            <button className="linkedin-button" onClick={handleLinkedInClick}>
              <svg className="linkedin-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
              Connect on LinkedIn
            </button>
          </div>
        </div>

        {/* Right Column - Inquiry Form */}
        <div className="contact-form-container">
          <h2>Have a query on our products?</h2>
          <p className="form-subtext">Fill in your details and we will get back to you shortly.</p>
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Your Phone Number"
                className={errors.phone ? 'error' : ''}
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Your City"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Enquiry Message *</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                placeholder="Write your message here..."
                className={errors.message ? 'error' : ''}
              ></textarea>
              {errors.message && <span className="error-message">{errors.message}</span>}
            </div>

            {/* Status Messages */}
            {submitStatus.message && (
              <div className={`status-message ${submitStatus.type}`}>
                {submitStatus.message}
              </div>
            )}

            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Submit Enquiry'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
